import {
  Archive, ArchiveRestore, GraduationCap, MoreHorizontal, Plus, Settings2,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader } from "@/components/layout/page";
import { SkillFormDialog } from "@/features/skill-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSkills, useTenants, useUpdateSkill } from "@/lib/queries";
import { catalogOrder } from "@/lib/skills";
import { atLeast, type RoutingSkill } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The skill catalog (ADR-015): what a line, a number or a handoff can require of the rep it rings.
 *
 * Setter, closer and Spanish are skills rather than queues or console roles, so a rep works every
 * line their skills fit. Supervisors read the catalog; admins add, edit, retire and restore. There
 * is no delete: lines, numbers, handoffs and reps name a skill by its slug, so retiring is
 * `active: false`. The ACD stops counting a retired skill within a minute, and every rep keeps their
 * level for the day it comes back.
 *
 * A rep's own skills are set from Users (`features/user-skills.tsx`), because they belong to the
 * phone of one person. A superadmin reads every tenant's catalog, so for them the tenant is a column.
 */
export function SkillsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";
  const { data, isPending, error, refetch } = useSkills();
  const { data: tenants } = useTenants({ enabled: isSuperadmin });
  const update = useUpdateSkill();

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<RoutingSkill | undefined>();
  const [retiring, setRetiring] = React.useState<RoutingSkill | undefined>();

  const openForm = (skill?: RoutingSkill) => {
    setEditing(skill);
    setFormOpen(true);
  };

  const setActive = async (skill: RoutingSkill, active: boolean) => {
    try {
      await update.mutateAsync({ id: skill.id, active });
      toast.success(active ? "Skill restored" : "Skill retired", skill.name);
      setRetiring(undefined);
    } catch (e) {
      toast.error(
        active ? "Could not restore the skill" : "Could not retire the skill",
        e instanceof ApiError ? e.message : "Please try again.",
      );
    }
  };

  const newButton = canManage ? (
    <Button variant="default" onClick={() => openForm()}>
      <Plus />
      New skill
    </Button>
  ) : undefined;

  return (
    <Page>
      <PageHeader
        title="Skills"
        description={
          <>
            What a line, a number or a handoff can require of the rep it rings. The ACD rings only a
            rep who holds each skill a call requires, at the level it asks. A rep's own skills are
            set on{" "}
            <Link to="/users" className="text-accent-11 underline-offset-4 hover:underline">
              Users
            </Link>
            .
          </>
        }
        action={newButton}
      />

      <Card className="overflow-hidden">
        {isPending ? (
          <TableSkeleton cols={6} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load skills"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={GraduationCap}
            title="No skills yet"
            description="Add what your lines route on: setter, closer, a language. Until one exists, a call cannot require anything of the rep it rings."
            action={newButton}
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Used as</TableHead>
                <TableHead>Reps</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {catalogOrder(data).map((s) => (
                <TableRow key={s.id} className="[&>td]:h-11">
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      <span className={cn("font-medium", s.active ? "text-text" : "text-text-muted")}>
                        {s.name}
                      </span>
                      {!s.active && (
                        <Badge tone="outline" size="sm">
                          Retired
                        </Badge>
                      )}
                    </span>
                    {s.description && (
                      <p className="max-w-md text-xs text-text-muted">{s.description}</p>
                    )}
                  </TableCell>
                  <MonoCell>
                    <span className="inline-flex items-center gap-1">
                      {s.slug}
                      <CopyButton value={s.slug} label="Copy slug" />
                    </span>
                  </MonoCell>
                  <TableCell className="text-text-muted">{s.category ?? "—"}</TableCell>
                  <TableCell>
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {s.is_agent_skill && (
                        <Badge tone="accent" size="sm">
                          Rep skill
                        </Badge>
                      )}
                      {s.is_lead_tag && (
                        <Badge tone="neutral" size="sm">
                          Lead tag
                        </Badge>
                      )}
                      {!s.is_agent_skill && !s.is_lead_tag && (
                        <span className="text-xs text-text-subtle">Nothing</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge tone={s.holders > 0 ? "neutral" : "outline"}>{s.holders}</Badge>
                  </TableCell>
                  {isSuperadmin && (
                    <TableCell className="text-xs text-text-muted">
                      {tenants?.find((t) => t.id === s.tenant_id)?.name ?? "—"}
                    </TableCell>
                  )}
                  <TableCell>
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${s.name}`}>
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onSelect={() => openForm(s)}>
                            <Settings2 />
                            Edit skill
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {s.active ? (
                            <DropdownMenuItem onSelect={() => setRetiring(s)}>
                              <Archive />
                              Retire
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onSelect={() => void setActive(s, true)}>
                              <ArchiveRestore />
                              Restore
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <SkillFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        skill={editing}
        catalog={data ?? []}
      />

      <ConfirmDialog
        open={Boolean(retiring)}
        onOpenChange={(open) => !open && setRetiring(undefined)}
        title="Retire this skill?"
        description={
          <>
            <span className="font-medium">{retiring?.name}</span> stops counting within a minute. A
            line or a number that still requires it has nobody to ring until its relax timer, if it
            has one, lifts the requirement. {holdersNote(retiring?.holders ?? 0)}
          </>
        }
        confirmLabel="Retire skill"
        loading={update.isPending}
        onConfirm={() => {
          if (retiring) void setActive(retiring, false);
        }}
      />
    </Page>
  );
}

/** What retiring does to the reps holding the skill: nothing they lose, unless their set is saved. */
function holdersNote(holders: number): string {
  if (holders === 0) return "Nobody holds it.";
  const who = holders === 1 ? "The rep who holds it keeps their level" : `The ${holders} reps who hold it keep their levels`;
  return `${who} for the day it is restored, unless their skills are saved while it is retired.`;
}
